import { forwardRef } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { Badge } from '../../atoms/Badge/Badge';
import { Button } from '../../atoms/Button/Button';
import { Card } from '../../atoms/Card/Card';
import { Checkbox } from '../../atoms/Checkbox';
import { Inline } from '../../atoms/Inline/Inline';
import { Input } from '../../atoms/Input/Input';
import { Select } from '../../atoms/Select/Select';
import { Stack } from '../../atoms/Stack/Stack';
import { Heading } from '../../atoms/Typography/Heading';
import { Text } from '../../atoms/Typography/Text';
import { composeScientificName } from '../LivestockIdentifyFlow/scientificName';
import type {
  OverviewCandidate,
  OverviewCategory,
  OverviewSuggestion,
} from '../../../services/livestockOverviewService';
import './LivestockOverview.css';

export interface CandidateDraft {
  selected: boolean;
  commonName: string;
  scientificName: string;
  category: OverviewCategory;
}

export interface OverviewCandidateCardProps {
  candidate: OverviewCandidate;
  draft: CandidateDraft;
  /** Validation message of the name field (shown only for selected animals). */
  nameError?: string;
  /** Local change (typing). */
  onChange: (patch: Partial<CandidateDraft>) => void;
  /** Change to keep (toggle, category, chosen suggestion, name field left). */
  onCommit: (patch: Partial<CandidateDraft>) => void;
  disabled?: boolean;
}

const CATEGORIES: OverviewCategory[] = ['fish', 'coral', 'invertebrate', 'other'];
const CONFIDENCE_TONE = { high: 'success', medium: 'info', low: 'warning' } as const;

function toCategory(value: string): OverviewCategory {
  return (CATEGORIES as string[]).includes(value) ? (value as OverviewCategory) : 'other';
}

/** The draft fields a suggestion fills in. */
export function draftFromSuggestion(suggestion: OverviewSuggestion): Pick<CandidateDraft, 'commonName' | 'scientificName' | 'category'> {
  const scientificName = composeScientificName(suggestion.taxonomy);
  return {
    commonName: suggestion.commonNames[0]?.trim() || scientificName,
    scientificName,
    category: toCategory(suggestion.category),
  };
}

/**
 * One found animal: its cut-out, the suggested name (editable), the category, the
 * confidence as text and a checkbox that decides whether it is added.
 */
export const OverviewCandidateCard = forwardRef<HTMLInputElement, OverviewCandidateCardProps>(function OverviewCandidateCard(
  { candidate, draft, nameError, onChange, onCommit, disabled = false },
  nameRef,
) {
  const { t } = useTranslation();
  const number = candidate.position;
  const suggestions = candidate.suggestions ?? [];
  const best = suggestions[0];
  const fieldsDisabled = disabled || candidate.created;

  return (
    <Card padding="lg" data-testid="overview-candidate">
      <div className="livestock-overview__candidate">
        {candidate.imageUrl ? (
          <img className="livestock-overview__crop" src={candidate.imageUrl} alt={t('livestockOverview.review.cropAlt', { number })} />
        ) : (
          <span aria-hidden="true" className="livestock-overview__crop" />
        )}
        <Stack gap="3">
          <Inline gap="2" align="center" wrap>
            <Heading level={3}>{t('livestockOverview.review.number', { number })}</Heading>
            {best ? (
              <Badge tone={CONFIDENCE_TONE[best.confidence]}>{t(`livestockOverview.review.confidence.${best.confidence}`)}</Badge>
            ) : (
              <Badge tone="neutral">{t('livestockOverview.review.confidence.none')}</Badge>
            )}
            {candidate.created && <Badge tone="success">{t('livestockOverview.review.created')}</Badge>}
          </Inline>
          {candidate.detectedLabel && (
            <Text size="sm" color="muted">{t('livestockOverview.review.detectedAs', { label: candidate.detectedLabel })}</Text>
          )}

          {!candidate.created && (
            <Checkbox
              id={`overview-${candidate.id}-selected`}
              label={t('livestockOverview.review.select', { number })}
              checked={draft.selected}
              disabled={disabled}
              onChange={(event) => onCommit({ selected: event.target.checked })}
            />
          )}

          <div className="livestock-overview__fields">
            <Input
              ref={nameRef}
              id={`overview-${candidate.id}-name`}
              label={t('livestockOverview.review.commonName')}
              value={draft.commonName}
              maxLength={255}
              disabled={fieldsDisabled}
              error={nameError}
              required={draft.selected && !candidate.created}
              onChange={(event) => onChange({ commonName: event.target.value })}
              onBlur={(event) => onCommit({ commonName: event.target.value.trim() })}
            />
            <Input
              id={`overview-${candidate.id}-scientific-name`}
              label={t('livestockOverview.review.scientificName')}
              value={draft.scientificName}
              maxLength={255}
              disabled={fieldsDisabled}
              onChange={(event) => onChange({ scientificName: event.target.value })}
              onBlur={(event) => onCommit({ scientificName: event.target.value.trim() })}
            />
            <Select
              id={`overview-${candidate.id}-category`}
              label={t('livestockOverview.review.category')}
              value={draft.category}
              disabled={fieldsDisabled}
              onChange={(event) => onCommit({ category: toCategory(event.target.value) })}
              options={CATEGORIES.map((value) => ({ value, label: t(`livestock.categories.${value}`) }))}
            />
          </div>

          {best?.featureExplanation && (
            <Text size="sm" color="muted">{t('livestockOverview.review.reason', { reason: best.featureExplanation })}</Text>
          )}

          {!candidate.created && suggestions.length > 1 && (
            <Stack gap="2">
              <Text size="sm" weight="semibold">{t('livestockOverview.review.alternatives')}</Text>
              <Inline gap="2" wrap>
                {suggestions.slice(1).map((suggestion, index) => {
                  const name = draftFromSuggestion(suggestion).commonName;
                  return (
                    <Button
                      key={`${name}-${index}`}
                      variant="ghost"
                      size="sm"
                      disabled={disabled}
                      onClick={() => onCommit(draftFromSuggestion(suggestion))}
                    >
                      {t('livestockOverview.review.useAlternative', { name })}
                    </Button>
                  );
                })}
              </Inline>
            </Stack>
          )}
        </Stack>
      </div>
    </Card>
  );
});

export default OverviewCandidateCard;
